/**
 * Per-channel outbound text limits — single source of truth.
 *
 * Isomorphic on purpose (type-only import, no server/node deps) so adapters,
 * the send pipeline, and client UI components can all read the same values
 * without the client pulling in server adapter/registry code.
 *
 * Total `Record<ChannelType, ...>` (not Partial) matches the existing
 * display-map convention (`CHANNEL_LABELS`, `CHANNEL_CONFIG`): adding a new
 * ChannelType fails typecheck here, forcing a deliberate limit decision.
 */

import type { ChannelType } from '../types'

export type TextLimitUnit = 'bytes' | 'chars'

export interface ChannelTextLimit {
  /** Maximum size of a single outbound message, in `unit`. */
  max: number
  /**
   * Instagram enforces a byte limit (UTF-8); Messenger counts characters.
   * Drives how the segmenter measures — bytes via TextEncoder, chars via code
   * points. Splitting is always grapheme-safe regardless of unit.
   */
  unit: TextLimitUnit
  /**
   * true  — segment outbound text to fit `max` before sending (transport with a
   *         hard platform limit: instagram, facebook). The operator composer
   *         also surfaces a length counter for these channels.
   * false — advisory only: no segmentation (web channels render markdown and
   *         their `sendMessage` is a no-op, poll-based). `max`/`unit` are kept
   *         as capability descriptors but are NOT surfaced in the composer —
   *         there is no hard limit to warn about.
   */
  enforceOnSend: boolean
}

export const CHANNEL_TEXT_LIMITS: Record<ChannelType, ChannelTextLimit> = {
  instagram: { max: 1000, unit: 'bytes', enforceOnSend: true },
  facebook: { max: 2000, unit: 'chars', enforceOnSend: true },
  web_widget: { max: 2000, unit: 'chars', enforceOnSend: false },
  web_demo: { max: 2000, unit: 'chars', enforceOnSend: false },
  telegram: { max: 4096, unit: 'chars', enforceOnSend: true },
  whatsapp: { max: 4096, unit: 'chars', enforceOnSend: true },
  // TikTok's documented DM limit, counted in characters including spaces and
  // emojis. Large enough that segmentation effectively never fires here.
  tiktok: { max: 6000, unit: 'chars', enforceOnSend: true },
}
